import getBanks from 'api/v1/transactions/getBank';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useQuery from 'hooks/react-query/useQuery';
import { type UseQueryResult } from '@tanstack/react-query';
import {
  BankStatus,
  BankFieldType,
  BankProviders,
  type Bank,
} from 'types/banks.types';
import { BanksCacheKeys } from 'types/cacheKeys.types';
import type { AvailableLanguagesBE } from 'types/users.types';
import type { PriorityBEBankMeta } from 'utils/constants';
import { BEBanksList } from 'utils/constants';
import { isTester } from 'utils/helpers';
import vividLogo from 'assets/images/BanksIcons/vivid_l.png';

// Hardcoded Vivid bank entry
const vividBank: Bank = {
  id: 'vivid-hardcoded',
  name: 'Vivid',
  provider: BankProviders.tink,
  fields: [
    {
      type: BankFieldType.iban,
      name: 'IBAN',
    },
  ],
  bic: 'SOLARISB',
  status: BankStatus.stable,
  maintenance: false,
  popular: true,
  groupDisplayName: 'Vivid',
  financialInstitutionSlug: 'vivid',
  financialInstitutionName: 'Vivid',
  logoUrl: vividLogo,
};

const useGetBanks = () => {
  const customer = useGetCurrentCustomerData().data;
  const { i18n } = useTranslation();
  const lang = i18n.language as AvailableLanguagesBE;
  const provider = useFeatures('banks.provider') as BankProviders;

  const priorityMap = useMemo(() => {
    return (BEBanksList[lang] || []).reduce((acc, bankMeta, index) => {
      return { ...acc, [bankMeta.slug]: { ...bankMeta, index } };
    }, {} as { [key: string]: PriorityBEBankMeta & { index: number } });
  }, [lang]);

  return useQuery(
    [BanksCacheKeys.BANKS_LIST, { lang, isTester: isTester(customer) }],
    () =>
      getBanks({ country: customer?.country, provider }).then((list) => {
        const _list = isTester(customer)
          ? list
          : list.filter((bank) => bank.status !== BankStatus.dev);
        return [..._list, vividBank]
          .map((bank) => {
            if (!bank.financialInstitutionSlug) {
              return { ...bank, index: Infinity };
            }
            const withMeta = priorityMap[bank.financialInstitutionSlug];
            if (!withMeta) {
              return { ...bank, index: Infinity };
            }
            return {
              ...bank,
              index: withMeta.index,
              fullLogoUrl: withMeta.img,
            };
          })
          .sort((a, b) => {
            const A = a.index;
            const B = b.index;
            if (A < B) return -1;
            if (A > B) return 1;
            return 0;
          });
      }),
  );
};

export default useGetBanks;
